import React, { useState, useEffect } from 'react';
import { http } from '../lib/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  LayoutDashboard, 
  Calendar, 
  CheckCircle2, 
  Clock, 
  Wrench, 
  AlertTriangle, 
  TrendingUp, 
  Users, 
  Activity,
  FileText,
  ArrowRight,
  RefreshCw,
  Building,
  User
} from 'lucide-react';
import OrdenDetalleModal from './ordenes/OrdenDetalleModal';

// Componente para tarjeta de métrica
const MetricCard = ({ title, value, subtitle, type = 'default', icon: Icon, trend }) => {
  const getCardStyles = () => {
    switch (type) {
      case 'success':
        return 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-100 text-emerald-900';
      case 'warning':
        return 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100 text-amber-900';
      case 'error':
        return 'bg-gradient-to-br from-red-50 to-rose-50 border-red-100 text-red-900';
      case 'info':
        return 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-100 text-emerald-900';
      default:
        return 'bg-white border-gray-100 text-gray-800';
    }
  };

  const getIconStyles = () => {
    switch (type) {
      case 'success': return 'bg-emerald-100 text-emerald-600';
      case 'warning': return 'bg-amber-100 text-amber-600';
      case 'error': return 'bg-red-100 text-red-600';
      case 'info': return 'bg-emerald-100 text-emerald-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className={`p-6 rounded-2xl border shadow-sm transition-all duration-200 hover:shadow-md ${getCardStyles()}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium opacity-70 mb-1">{title}</p>
          <h3 className="text-3xl font-bold tracking-tight">{value}</h3>
          {subtitle && (
            <p className="text-sm opacity-60 mt-2 font-medium">{subtitle}</p>
          )}
        </div>
        {Icon && (
          <div className={`p-3 rounded-xl ${getIconStyles()}`}>
            <Icon className="w-6 h-6" />
          </div>
        )}
      </div>
      {trend && (
        <div className="mt-4 flex items-center text-sm font-medium">
          <span className={`flex items-center ${trend.positive ? 'text-emerald-600' : 'text-red-600'}`}>
            {trend.positive ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingUp className="w-4 h-4 mr-1 rotate-180" />}
            {trend.value}
          </span>
          <span className="ml-2 opacity-50">{trend.period}</span>
        </div>
      )}
    </div>
  );
};

// Componente para alertas
const AlertPanel = ({ alertas }) => {
  const getAlertStyles = (tipo) => {
    switch (tipo) {
      case 'error':
        return 'bg-red-50 border-red-100 text-red-900';
      case 'warning':
        return 'bg-amber-50 border-amber-100 text-amber-900';
      case 'info':
        return 'bg-emerald-50 border-emerald-100 text-emerald-900';
      default:
        return 'bg-gray-50 border-gray-100 text-gray-900';
    }
  };

  const getPriorityIcon = (prioridad) => {
    switch (prioridad) {
      case 'critica': return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case 'alta': return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'media': return <Activity className="w-5 h-5 text-yellow-500" />;
      case 'baja': return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      default: return <Activity className="w-5 h-5 text-gray-400" />;
    }
  };

  if (!alertas || alertas.length === 0) {
    return (
      <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-6 flex items-center gap-4">
        <div className="p-3 bg-emerald-100 rounded-full text-emerald-600">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <div>
          <h4 className="font-semibold text-emerald-900">Sistema Operando Normalmente</h4>
          <p className="text-emerald-700 text-sm">No hay alertas activas en este momento</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {alertas.map((alerta, index) => (
        <div
          key={index}
          className={`p-4 rounded-xl border flex items-start gap-4 ${getAlertStyles(alerta.tipo)}`}
        >
          <div className="mt-1">{getPriorityIcon(alerta.prioridad)}</div>
          <div className="flex-1">
            <h4 className="font-semibold text-sm">{alerta.titulo}</h4>
            <p className="text-sm mt-1 opacity-90">{alerta.mensaje}</p>
            {alerta.accion && (
              <div className="mt-2 inline-flex items-center text-xs font-medium bg-white/50 px-2 py-1 rounded border border-black/5">
                📋 Acción: {alerta.accion}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Componente para métricas de técnicos
const TecnicosPanel = ({ tecnicos }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'ocupado': return 'bg-rose-100 text-rose-700';
      case 'disponible': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getCargaColor = (carga) => {
    switch (carga) {
      case 'alta': return 'bg-rose-500';
      case 'media': return 'bg-amber-500';
      case 'baja': return 'bg-emerald-500';
      default: return 'bg-gray-300';
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {tecnicos.map((tecnico) => (
        <div key={tecnico.id} className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-bold">
                {tecnico.nombre.charAt(0)}
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">{tecnico.nombre}</h4>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(tecnico.status)}`}>
                  {tecnico.status}
                </span>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-4 gap-2 mb-4 divide-x divide-gray-100">
            <div className="text-center px-1">
              <p className="text-lg font-bold text-emerald-600">{tecnico.ordenes_mes}</p>
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Total</p>
            </div>
            <div className="text-center px-1">
              <p className="text-lg font-bold text-emerald-600">{tecnico.completadas_mes}</p>
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Listas</p>
            </div>
            <div className="text-center px-1">
              <p className="text-lg font-bold text-amber-600">{tecnico.agendamientos_pendientes}</p>
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Pend.</p>
            </div>
            <div className="text-center px-1">
              <p className="text-lg font-bold text-purple-600">{tecnico.eficiencia}%</p>
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Efic.</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between pt-3 border-t border-gray-50">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Carga Actual</span>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${getCargaColor(tecnico.carga_trabajo)}`}></div>
              <span className="text-sm font-medium text-gray-700 capitalize">{tecnico.carga_trabajo}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Componente principal del Dashboard
const Dashboard = () => {
  const [kpis, setKpis] = useState(null);
  const [tecnicos, setTecnicos] = useState([]);
  const [tendencias, setTendencias] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Función para cargar todos los datos del dashboard
  const cargarDatosDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      // Cargar datos en paralelo
      const [kpisRes, tecnicosRes, tendenciasRes, alertasRes, ordenesRes] = await Promise.all([
        http.get('/dashboard/kpis-tiempo-real'),
        http.get('/dashboard/metricas-tecnicos'),
        http.get('/dashboard/tendencias'),
        http.get('/dashboard/alertas-activas'),
        http.get('/ordenes') // Fetch all orders to slice locally for "Recent"
      ]);

      setKpis(kpisRes.data.data);
      setTecnicos(tecnicosRes.data.data);
      setTendencias(tendenciasRes.data.data);
      setAlertas(alertasRes.data.data);
      
      // Process recent orders
      const allOrders = ordenesRes.data || [];
      // Sort by date descending and take top 5
      const sortedOrders = allOrders.sort((a, b) => {
        const dateA = new Date(a.created_at || a.fecha);
        const dateB = new Date(b.created_at || b.fecha);
        return dateB - dateA;
      }).slice(0, 5);
      setRecentOrders(sortedOrders);

      setLastUpdate(new Date());

    } catch (err) {
      console.error('Error cargando dashboard:', err);
      setError('Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos inicialmente y configurar auto-refresh
  useEffect(() => {
    cargarDatosDashboard();

    // Auto-refresh cada 30 segundos
    const interval = setInterval(cargarDatosDashboard, 30000);

    return () => clearInterval(interval);
  }, []);

  // Preparar datos para gráficos
  const datosEstados = kpis ? [
    { name: 'Pendientes', value: kpis.agendamientos.pendientes, color: '#fbbf24' },
    { name: 'En Progreso', value: kpis.agendamientos.en_progreso, color: '#3b82f6' },
    { name: 'Completados', value: kpis.agendamientos.completados, color: '#10b981' }
  ] : [];

  if (loading && !kpis) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6 bg-white rounded-2xl shadow-lg">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error de Conexión</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={cargarDatosDashboard}
            className="bg-emerald-600 text-white px-6 py-2.5 rounded-xl hover:bg-emerald-700 font-medium transition-colors w-full"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50/50 min-h-screen max-w-[1600px] mx-auto space-y-8">
      {/* Header del Dashboard */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <LayoutDashboard className="w-8 h-8 text-emerald-600" />
            Dashboard Ejecutivo
          </h1>
          <p className="text-gray-500 mt-1 text-lg">Monitoreo en tiempo real del sistema Elements</p>
        </div>
        <div className="flex items-center gap-4 bg-white p-2 rounded-xl border border-gray-100 shadow-sm">
          {lastUpdate && (
            <div className="px-3 text-right border-r border-gray-100">
              <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Última actualización</p>
              <p className="text-sm font-medium text-gray-700">{lastUpdate.toLocaleTimeString()}</p>
            </div>
          )}
          <button
            onClick={cargarDatosDashboard}
            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
            disabled={loading}
            title="Actualizar datos"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* KPIs Principales */}
      {kpis && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Agendamientos Hoy"
            value={kpis.agendamientos.hoy}
            subtitle={`${kpis.agendamientos.mes_actual} este mes`}
            type={kpis.agendamientos.hoy > 0 ? 'info' : 'default'}
            icon={Calendar}
          />
          <MetricCard
            title="Órdenes Completadas"
            value={kpis.ordenes.completadas_mes}
            subtitle={`${kpis.ordenes.eficiencia}% eficiencia`}
            type={kpis.ordenes.eficiencia > 80 ? 'success' : kpis.ordenes.eficiencia > 60 ? 'warning' : 'error'}
            icon={CheckCircle2}
          />
          <MetricCard
            title="Utilización de Horas"
            value={`${kpis.horas.utilizacion}%`}
            subtitle={`${kpis.horas.disponibles}h disponibles`}
            type={kpis.horas.utilizacion > 90 ? 'warning' : kpis.horas.utilizacion > 100 ? 'error' : 'success'}
            icon={Clock}
          />
          <MetricCard
            title="Eficiencia Mantenimiento"
            value={`${kpis.mantenimiento.eficiencia}%`}
            subtitle={`${kpis.mantenimiento.equipos_mantenidos_mes}/${kpis.mantenimiento.equipos_totales} equipos`}
            type={kpis.mantenimiento.eficiencia > 80 ? 'success' : kpis.mantenimiento.eficiencia > 60 ? 'warning' : 'error'}
            icon={Wrench}
          />
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* Columna Izquierda (2/3) */}
        <div className="xl:col-span-2 space-y-8">
          
          {/* Gráficos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Gráfico de Estados */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Activity className="w-5 h-5 text-emerald-500" />
                Estados de Agendamientos
              </h3>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={datosEstados}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {datosEstados.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-4 mt-4">
                {datosEstados.map((entry, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></div>
                    <span className="text-gray-600">{entry.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Gráfico de Tendencias */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                Tendencias Semanales
              </h3>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={tendencias}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="semana" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip 
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="agendamientos" 
                      stroke="#3b82f6" 
                      strokeWidth={3}
                      dot={{ r: 4, fill: '#3b82f6', strokeWidth: 2, stroke: '#fff' }}
                      activeDot={{ r: 6 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="ordenes_completadas" 
                      stroke="#10b981" 
                      strokeWidth={3}
                      dot={{ r: 4, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Panel de Técnicos */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-teal-600" />
              Estado del Equipo Técnico
            </h2>
            <TecnicosPanel tecnicos={tecnicos} />
          </div>

        </div>

        {/* Columna Derecha (1/3) */}
        <div className="space-y-8">
          
          {/* Alertas Activas */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Alertas del Sistema
            </h2>
            <AlertPanel alertas={alertas} />
          </div>

          {/* Órdenes Recientes */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-500" />
                Órdenes Recientes
              </h2>
            </div>
            <div className="divide-y divide-gray-50">
              {recentOrders.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No hay órdenes recientes
                </div>
              ) : (
                recentOrders.map((orden) => (
                  <div 
                    key={orden._id || orden.id}
                    onClick={() => setSelectedOrder(orden)}
                    className="p-4 hover:bg-emerald-50/50 transition-colors cursor-pointer group"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-medium text-gray-900 group-hover:text-emerald-700 transition-colors">
                        #{orden.numero_orden || 'S/N'}
                      </span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${
                        orden.estado === 'completada' ? 'bg-green-100 text-green-700' :
                        orden.estado === 'en_proceso' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {orden.estado?.replace('_', ' ') || 'Pendiente'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                      <Building className="w-3.5 h-3.5 text-gray-400" />
                      <span className="truncate">{orden.nombre_cliente || 'Cliente desconocido'}</span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <User className="w-3 h-3" />
                        <span>{orden.nombre_tecnico || 'Sin asignar'}</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-emerald-400 transition-colors" />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Modal de Detalles de Orden */}
      {selectedOrder && (
        <OrdenDetalleModal
          orden={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onUpdate={() => {
            cargarDatosDashboard();
            setSelectedOrder(null);
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;